import type React from "react";
import { useTranslation } from "react-i18next";
import { FaCircle, FaStop } from "react-icons/fa6";
import { Loading } from "../Loading";

interface RecordButtonProps {
  height: number;
  compact?: boolean;
  onLive: boolean;
  isLoading?: boolean;
  disabled: boolean;
  showAssembler: boolean;
  onStart: () => void;
  onStop: () => void;
}

const NO_DRAG_REGION_CLASS = "[-webkit-app-region:no-drag]";

export const RecordButton: React.FC<RecordButtonProps> = ({
  height,
  compact = false,
  onLive,
  isLoading = false,
  disabled,
  showAssembler,
  onStart,
  onStop,
}) => {
  const { t } = useTranslation("dashboard");

  const label = isLoading
    ? t("DASHBOARD.starting")
    : onLive
      ? t("DASHBOARD.powerOff")
      : t("DASHBOARD.MENUBAR.STREAMING.record");
  const tooltip = isLoading
    ? t("DASHBOARD.starting")
    : onLive
      ? t("DASHBOARD.powerOff")
      : showAssembler
        ? t("DASHBOARD.broadcastRequiresPreview")
        : t("DASHBOARD.MENUBAR.STREAMING.record");

  const buttonHeight = Math.max(24, Math.min(32, height - 6));

  const className = [
    NO_DRAG_REGION_CLASS,
    "inline-flex items-center justify-center gap-1.5 rounded-full border px-2.5 sm:px-3",
    "text-[11px] font-semibold tracking-[0.02em] ",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-400/40",
    "disabled:cursor-not-allowed disabled:opacity-45 disabled:hover:translate-y-0",
    compact ? "w-8 px-0 sm:w-9" : "",
    onLive
      ? "border-red-400/45 bg-[linear-gradient(180deg,rgba(239,68,68,0.92),rgba(220,38,38,0.94))] text-white shadow-[0_0_0_1px_rgba(248,113,113,0.24),0_10px_20px_-14px_rgba(239,68,68,0.9)]"
      : "border-sky-400/35 bg-sky-500/12 text-sky-100 hover:border-sky-300/55 hover:bg-sky-500/18",
  ].join(" ");

  return (
    <button
      type="button"
      className={className}
      style={{ height: `${buttonHeight}px` }}
      aria-label={label}
      title={tooltip}
      disabled={(disabled && !onLive) || isLoading}
      onClick={onLive ? onStop : onStart}
    >
      {isLoading ? (
        <Loading size={12} className="text-current" />
      ) : onLive ? (
        <FaStop size={10} />
      ) : (
        <FaCircle size={9} />
      )}
      {!compact && <span>{label}</span>}
    </button>
  );
};
